import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Calendar, MapPin, Clock, ChevronRight, ChevronLeft, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useEventsByKeywords } from '../../hooks/useEventsByKeywords';
import { KeywordEvent } from '../../api/keywordsApi';
import type { Section } from '../../types/teamContentPage.types';
import { SearchBar } from '../common/SearchBar';
import { useDebounce } from '../../hooks/useDebounce';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useEventsCache } from '../../contexts/EventsCacheContext';

interface EventsSectionProps {
  section: Section;
}

export const EventsSection: React.FC<EventsSectionProps> = ({ section }) => {
  // Extract keyword IDs from section
  const keywordIds = section.keywords?.map(k => k.id.toString()) || [];
  const includePast = section.sectionOption?.showPastEvents || false;

  // Cache context
  const { getCachedEvents, setCachedEvents } = useEventsCache();

  // Initialize state from cache if available
  const cachedData = getCachedEvents(keywordIds, includePast);
  
  // Search state
  const [searchValue, setSearchValue] = useState(cachedData?.searchValue || '');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Pagination state
  const [allEvents, setAllEvents] = useState<KeywordEvent[]>(cachedData?.events || []);
  const [currentPage, setCurrentPage] = useState(cachedData?.currentPage || 0);
  const [hasMoreEvents, setHasMoreEvents] = useState(cachedData?.hasMore ?? true);
  const eventsPerPage = 50;

  // Track if we have shown content before (for smooth transitions)
  const [hasInitialContent, setHasInitialContent] = useState(cachedData !== null);

  // Scroll state for horizontal mode
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Only fetch if:
  // 1. We don't have cached data at all
  // 2. Search changed from the cached search
  // 3. We're loading more pages than what's cached
  const shouldFetch = !cachedData || 
                     debouncedSearchValue !== cachedData.searchValue || 
                     currentPage > cachedData.currentPage;

  const { data: response, isLoading, error, isFetching } = useEventsByKeywords({
    keywordIds,
    includePast,
    limit: eventsPerPage,
    offset: currentPage * eventsPerPage,
    search: debouncedSearchValue,
    enabled: shouldFetch
  });

  // Track when we have initial content and accumulate events
  useEffect(() => {    
    if (response?.events) {
      if (currentPage === 0) {
        // First page or after search change - replace all events
        const newEvents = response.events;
        const newHasMore = response.meta?.hasMore || false;
        setAllEvents(newEvents);
        setHasMoreEvents(newHasMore);
        
        // Update cache with the new state
        setCachedEvents(keywordIds, includePast, {
          events: newEvents,
          totalCount: response.meta?.totalCount || 0,
          hasMore: newHasMore,
          currentPage: currentPage,
          searchValue: debouncedSearchValue,
          lastFetchTime: Date.now()
        });
      } else {
        // Subsequent pages - append to existing events
        setAllEvents(prev => {
          const newEvents = [...prev, ...response.events];
          const newHasMore = response.meta?.hasMore || false;
          
          // Update cache with the new state
          setCachedEvents(keywordIds, includePast, {
            events: newEvents,
            totalCount: response.meta?.totalCount || 0,
            hasMore: newHasMore,
            currentPage: currentPage,
            searchValue: debouncedSearchValue,
            lastFetchTime: Date.now()
          });
          
          return newEvents;
        });
        setHasMoreEvents(response.meta?.hasMore || false);
      }
      
      if (response.events.length > 0) {
        setHasInitialContent(true);
      }
    }
  }, [response, currentPage, keywordIds, includePast, debouncedSearchValue, setCachedEvents]);

  // Reset pagination when search changes (clears cache for this search)
  useEffect(() => {
    if (debouncedSearchValue !== cachedData?.searchValue) {
      setCurrentPage(0);
      setAllEvents([]);
      setHasMoreEvents(true);
    }
  }, [debouncedSearchValue]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent form submission on Enter key
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Also stop propagation to parent elements
    }
  }, []);

  // Global form submit handler to prevent any accidental submissions
  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  // Load more handler
  const handleLoadMore = useCallback(() => {
    setCurrentPage(prev => prev + 1);
  }, []);

  // Get display options from section
  const isGridView = section.sectionOption?.displayStyle !== 'list';
  const showImages = section.sectionOption?.showImages !== false;
  const columns = section.sectionOption?.columns || 3;

  // Determine if we're in half-width mode
  const isHalfWidth = !section.sectionOption?.fullWidth;

  // Map columns to grid classes (Tailwind doesn't support dynamic classes)
  const getGridClass = () => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 4: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
      default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'; // 3 columns
    }
  };

  // Check scroll position and update button visibility
  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Scroll handlers
  const scrollLeft = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const cardWidth = 350; // Minimum card width
    scrollContainerRef.current.scrollBy({ left: -cardWidth * 2, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const cardWidth = 350; // Minimum card width
    scrollContainerRef.current.scrollBy({ left: cardWidth * 2, behavior: 'smooth' });
  }, []);

  // Monitor scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();
    container.addEventListener('scroll', checkScrollPosition);

    // Check on resize
    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [checkScrollPosition, allEvents]); // Re-run when allEvents change

  // Show initial loading state only when no content has been shown yet
  if (isLoading && !hasInitialContent) {
    return (
      <div>
        {/* Search Bar */}
        <div className="mb-6">
          <SearchBar
            value={searchValue}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            placeholder="Search events by name..."
            className="w-full"
            disabled
          />
        </div>

        {/* Skeleton Loader */}
        <EventsSkeleton
          isGridView={section.sectionOption?.displayStyle !== 'list'}
          isHalfWidth={!section.sectionOption?.fullWidth}
          columns={section.sectionOption?.columns || 3}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p>Unable to load events. Please try again later.</p>
        </div>
      </div>
    );
  }

  // Show nothing if we haven't received any response yet and don't have cached data
  if (!response && !isLoading && allEvents.length === 0 && !cachedData) {
    return null;
  }
  
  // Only show empty state if we've received a response and there are no events
  if ((!allEvents || allEvents.length === 0) && !isLoading && (response || cachedData)) {
    return (
      <>
        <style>
          {`
            /* Loading overlay styles */
            .loading-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(255, 255, 255, 0.8);
              backdrop-filter: blur(2px);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10;
              opacity: 0;
              animation: fadeIn 0.2s ease-in-out forwards;
            }
            @keyframes fadeIn {
              to {
                opacity: 1;
              }
            }
          `}
        </style>
        <div onSubmit={handleFormSubmit}>
          {/* Search Bar - Wrapped to prevent any accidental form submission */}
          <div className="mb-6" onSubmit={handleFormSubmit}>
            <SearchBar
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              placeholder="Search events by name..."
              className="w-full"
            />
          </div>

          {/* Empty State with Loading Overlay */}
          <div className="relative">
            {isFetching && hasInitialContent && (
              <div className="loading-overlay">
                <LoadingSpinner size="lg" />
              </div>
            )}

            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">
                {searchValue.trim()
                  ? `No events found matching "${searchValue}"`
                  : 'No upcoming events found.'}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
          .event-description-content strong,
          .event-description-content b {
            font-weight: 600 !important;
          }
          .event-description-content em,
          .event-description-content i {
            font-style: italic !important;
          }
          .event-description-content u {
            text-decoration: underline !important;
          }
          /* Preserve inline styles from TinyMCE */
          .event-description-content [style*="color"] {
            /* Elements with inline color styles take precedence */
          }
          .event-description-content p {
            margin: 0;
          }
          /* Ensure line-clamp works with HTML content */
          .event-description-content.line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          /* Loading overlay styles */
          .loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(2px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            opacity: 0;
            animation: fadeIn 0.2s ease-in-out forwards;
          }
          @keyframes fadeIn {
            to {
              opacity: 1;
            }
          }
          /* Smooth content transitions */
          .events-content {
            transition: opacity 0.2s ease-in-out;
          }
          .events-content.loading {
            opacity: 0.5;
          }
        `}
      </style>
      <div onSubmit={handleFormSubmit}>
        {/* Search Bar - Wrapped to prevent any accidental form submission */}
      <div className="mb-6" onSubmit={handleFormSubmit}>
        <SearchBar
          value={searchValue}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          placeholder="Search events by name..."
          className="w-full"
        />
      </div>

      {/* Events Container with Loading Overlay */}
      <div className="relative">
        {/* Show loading overlay when fetching and we have content */}
        {isFetching && hasInitialContent && (
          <div className="loading-overlay">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Events Grid/List */}
        <div className={`events-content ${isFetching ? 'loading' : ''}`}>
          {isGridView ? (
          isHalfWidth ? (
            // Half-width horizontal scroll mode
            <div className="relative">
            {/* Left Arrow */}
            {canScrollLeft && (
              <button
                onClick={scrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-5 w-5 text-gray-700" />
              </button>
            )}

            {/* Right Arrow */}
            {canScrollRight && (
              <button
                onClick={scrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-5 w-5 text-gray-700" />
              </button>
            )}

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {allEvents.map(event => (
                <div key={event.id} className="flex-shrink-0 w-[350px]">
                  <EventCard event={event} showImage={showImages} />
                </div>
              ))}
            </div>
            
            {/* Load More Button for horizontal scroll */}
            {hasMoreEvents && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isFetching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          // Full-width grid mode
          <div className={`grid ${getGridClass()} gap-6`}>
            {allEvents.map(event => (
              <EventCard key={event.id} event={event} showImage={showImages} />
            ))}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {allEvents.map(event => (
            <EventListItem key={event.id} event={event} showImage={showImages} />
          ))}
        </div>
      )}
        </div> {/* End of events-content */}
      </div> {/* End of relative container */}

      {/* Load More Button */}
      {hasMoreEvents && allEvents.length > 0 && (
        <div className="mt-8 text-center">
          <button
            onClick={handleLoadMore}
            disabled={isFetching}
            className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isFetching ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700 mr-3"></div>
                Loading...
              </>
            ) : (
              <>
                Load More Events
                <ChevronRight className="ml-2 h-5 w-5" />
              </>
            )}
          </button>
          {allEvents.length > 0 && response?.meta?.totalCount && (
            <p className="mt-2 text-sm text-gray-500">
              Showing {allEvents.length} of {response.meta.totalCount} events
            </p>
          )}
        </div>
      )}
    </div>
    </>
  );
};

// Event Card Component for Grid View
const EventCard: React.FC<{ event: KeywordEvent; showImage: boolean }> = ({ event, showImage }) => {
  const formatDateRange = (startDate: string, endDate: string) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (start.toDateString() === end.toDateString()) {
      return format(start, 'MMM d, yyyy');
    }

    if (start.getFullYear() === end.getFullYear()) {
      if (start.getMonth() === end.getMonth()) {
        return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
      }
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }

    return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  };

  const getEventStatus = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    if (now > end) {
      return { label: 'Past', className: 'bg-gray-100 text-gray-600' };
    } else if (now >= start && now <= end) {
      return { label: 'Live', className: 'bg-green-100 text-green-700' };
    } else {
      return { label: 'Upcoming', className: 'bg-blue-100 text-blue-700' };
    }
  };


  const status = getEventStatus(event.startDate, event.endDate);
  const eventUrl = `/${event.slug}`;

  return (
    <a href={eventUrl} className="block group h-full w-full">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
        {showImage && (
          <div className="relative h-48 bg-gray-100 overflow-hidden">
            {event.imageUrl ? (
              <>
                {/* Blurred background image */}
                <div
                  className="absolute inset-0 blur-lg scale-110 opacity-15"
                  style={{
                    backgroundImage: `url(${event.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                {/* Main image with aspect ratio preserved */}
                <img
                  src={event.imageUrl}
                  alt={event.name}
                  className="relative w-full h-full object-contain z-10"
                />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
                <Calendar className="h-16 w-16 text-white opacity-80" />
              </div>
            )}
            <div className="absolute top-3 right-3 z-20">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                {status.label}
              </span>
            </div>
          </div>
        )}

        <div className="p-5 flex-grow flex flex-col">
          <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600 transition-colors">
            {event.name}
          </h3>

          {event.description && (
            <div
              className="event-description-content text-gray-600 text-sm mb-3 line-clamp-2 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a:hover]:text-blue-700"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          )}

          <div className="space-y-2 mt-auto">
            <div className="flex items-center text-sm text-gray-500">
              <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{formatDateRange(event.startDate, event.endDate)}</span>
            </div>

            {event.location && (event.location.city || event.location.state) && (
              <div className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">
                  {[event.location.city, event.location.state].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <span className="text-blue-600 font-medium text-sm group-hover:text-blue-700">
              Learn More
            </span>
            <ChevronRight className="h-4 w-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </a>
  );
};

// Event List Item Component for List View
const EventListItem: React.FC<{ event: KeywordEvent; showImage: boolean }> = ({ event, showImage }) => {
  const formatDateTime = (startDate: string, endDate: string, startTime?: string, endTime?: string) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);

    let dateStr = '';
    if (start.toDateString() === end.toDateString()) {
      dateStr = format(start, 'MMM d, yyyy');
    } else {
      dateStr = `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }

    if (startTime) {
      const timeStr = startTime.substring(0, 5); // Get HH:MM
      dateStr += ` at ${timeStr}`;
      if (endTime && endTime !== startTime) {
        dateStr += ` - ${endTime.substring(0, 5)}`;
      }
    }

    return dateStr;
  };


  const eventUrl = `/${event.slug}`;

  return (
    <a href={eventUrl} className="block group">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
        <div className="flex gap-5">
          {showImage && (
            <div className="flex-shrink-0">
              {event.imageUrl ? (
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 relative">
                  {/* Blurred background for aspect ratio */}
                  <div
                    className="absolute inset-0 blur-sm scale-110"
                    style={{
                      backgroundImage: `url(${event.imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <img
                    src={event.imageUrl}
                    alt={event.name}
                    className="relative w-full h-full object-contain z-10"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Calendar className="h-8 w-8 text-white opacity-80" />
                </div>
              )}
            </div>
          )}

          <div className="flex-grow">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-lg group-hover:text-blue-600 transition-colors">
                {event.name}
              </h3>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all mt-0.5" />
            </div>

            {event.description && (
              <div
                className="event-description-content text-gray-600 text-sm mb-3 line-clamp-2 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a:hover]:text-blue-700"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            )}

            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1.5" />
                <span>{formatDateTime(event.startDate, event.endDate, event.startTime, event.endTime)}</span>
              </div>

              {event.location && (event.location.city || event.location.state) && (
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1.5" />
                  <span>
                    {[event.location.city, event.location.state].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

// Skeleton Loader Component
const EventsSkeleton: React.FC<{
  isGridView: boolean;
  isHalfWidth: boolean;
  columns: number;
}> = ({ isGridView, isHalfWidth, columns }) => {
  const getGridClass = () => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 4: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
      default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
  };

  if (!isGridView) {
    // List view skeleton
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 animate-pulse">
            <div className="flex gap-5">
              <div className="flex-shrink-0 w-24 h-24 bg-gray-200 rounded-lg"></div>
              <div className="flex-grow space-y-3">
                <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="flex gap-4">
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isHalfWidth) {
    // Horizontal scroll skeleton
    return (
      <div className="flex gap-6 overflow-hidden pb-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex-shrink-0 w-[350px]">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-5 space-y-3">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="space-y-2 mt-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grid view skeleton
  const skeletonCount = columns === 4 ? 4 : columns === 2 ? 4 : 6;
  return (
    <div className={`grid ${getGridClass()} gap-6`}>
      {[...Array(skeletonCount)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
          <div className="h-48 bg-gray-200"></div>
          <div className="p-5 space-y-3">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="space-y-2 mt-4">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventsSection;